import {INCOMPLETE_CONDITIONS} from '@chekin/core';

export type LockConditionKind =
  | 'payment'
  | 'registration'
  | 'verification'
  | 'checklist'
  | 'time';

const CONDITION_KINDS: Record<string, LockConditionKind> = {
  [INCOMPLETE_CONDITIONS.afterAllPaymentsComplete]: 'payment',
  [INCOMPLETE_CONDITIONS.afterAllGuestsRegisteredEnabled]: 'registration',
  [INCOMPLETE_CONDITIONS.afterLeadGuestRegisteredEnabled]: 'registration',
  [INCOMPLETE_CONDITIONS.afterAllGuestsPassedBiomatchEnabled]: 'verification',
  [INCOMPLETE_CONDITIONS.afterLeadGuestPassedBiomatchEnabled]: 'verification',
  [INCOMPLETE_CONDITIONS.allPaymentAndAllGuestsRegistered]: 'checklist',
  [INCOMPLETE_CONDITIONS.allPaymentAndAllGuestsIdentified]: 'checklist',
  [INCOMPLETE_CONDITIONS.allPaymentAndLeadGuestsIdentified]: 'checklist',
  [INCOMPLETE_CONDITIONS.onCheckInDayEnabled]: 'time',
};

export type ActionableConditionKind = Exclude<LockConditionKind, 'time'>;

export const CONDITION_ACTION_KEYS: Record<ActionableConditionKind, string> = {
  payment: 'pay',
  registration: 'register',
  verification: 'verify',
  checklist: 'open',
};

export type ActionableConditionRow = {
  condition: string;
  kind: ActionableConditionKind;
  isBlocking: boolean;
};

type AutomaticConditionRow = {
  condition: string;
  kind: 'time';
  isBlocking: false;
};

export type LockConditionRow = ActionableConditionRow | AutomaticConditionRow;

const SUPERSEDED_BY: Record<string, string> = {
  [INCOMPLETE_CONDITIONS.afterLeadGuestRegisteredEnabled]:
    INCOMPLETE_CONDITIONS.afterAllGuestsRegisteredEnabled,
  [INCOMPLETE_CONDITIONS.afterLeadGuestPassedBiomatchEnabled]:
    INCOMPLETE_CONDITIONS.afterAllGuestsPassedBiomatchEnabled,
  [INCOMPLETE_CONDITIONS.allPaymentAndLeadGuestsIdentified]:
    INCOMPLETE_CONDITIONS.allPaymentAndAllGuestsIdentified,
};

export function getConditionLeadHours(condition: string): number | undefined {
  if (condition === INCOMPLETE_CONDITIONS.onCheckInDayEnabled) return 0;
  if (condition.includes('one_week_before_check_in')) return 24 * 7;
  const hours = condition.match(/_(\d+)_hours_before_check_in/);
  return hours ? Number(hours[1]) : undefined;
}

function getConditionKind(condition: string): LockConditionKind {
  if (CONDITION_KINDS[condition]) return CONDITION_KINDS[condition];
  if (getConditionLeadHours(condition) !== undefined) return 'time';
  if (condition.includes('before_check_in')) return 'time';
  return 'checklist';
}

export function getConditionRows(conditions: string[] = []) {
  const actionable: ActionableConditionRow[] = [];
  const automatic: AutomaticConditionRow[] = [];
  const conditionSet = new Set(conditions);

  conditions
    .filter(condition => {
      const supersedingCondition = SUPERSEDED_BY[condition];
      return !supersedingCondition || !conditionSet.has(supersedingCondition);
    })
    .forEach(condition => {
      const kind = getConditionKind(condition);

      if (kind === 'time') {
        automatic.push({condition, kind, isBlocking: false});
        return;
      }

      actionable.push({condition, kind, isBlocking: false});
    });

  const [blocking] = actionable;
  if (blocking) {
    blocking.isBlocking = true;
  }

  return {rows: [...actionable, ...automatic] as LockConditionRow[], blocking};
}
